import { uniqBy } from 'lodash';
import { Difference } from 'microdiff';
import { ReactNode } from 'react';

import { Employees } from '~/data/employees';
import { OrganizationUnit } from '~/data/orgunits';
import {
  AuditOperation,
  AuditSummaryData,
  AuditSummaryData_Employee_Absences_Data,
  AuditSummaryData_EntityData,
  AuditTargetType,
  AuditType,
} from '~/data/userActivity/useGetAuditSummary';
import { resolveDataPointChanges } from '~/scenes/PeopleModule/components/EmployeeActions/EmployeeActivityLogDialog/components/EmployeeActivityLog/EmployeeChangeActivityLog/data/dataPoints';
import { convertToDateOrUndefined, getNumberOrUndefined } from '~/utils/helper';

export * from './useActivityLogDataTranslations';

export type UserActivityLogEventDataPoint = {
  key: string;
  label: string;
  value?: string | number | ReactNode;
  differenceType?: Difference['type'];
};

type UserActivityChangeEventsRow_Common<DataType> = {
  id: string;
  userId?: number;
  userName: string;
  userUnit: string;
  auditType: AuditType | string;
  operation: AuditOperation;
  timestamp?: Date;
  targetId?: number;
  targetName: string;
  targetUnit: string;
  data: DataType;
  dataPoints: UserActivityLogEventDataPoint[];
  dataPointChanges: UserActivityLogEventDataPoint[];
  changes?: Difference[];
  changeTrackingKey: string;
  transactionId?: string;
};

export type UserActivityChangeEventsRow_Employee =
  UserActivityChangeEventsRow_Common<object> & {
    targetType: AuditTargetType.Employee;
    targetSubType?: string;
  };

export type UserActivityChangeEventsRow_Entity =
  UserActivityChangeEventsRow_Common<AuditSummaryData_EntityData> & {
    targetType: AuditTargetType.Entity;
    targetSubType?: string;
  };

export type UserActivityChangeEventsRow_Other =
  UserActivityChangeEventsRow_Common<object> & {
    targetType: string;
    targetSubType?: string;
  };

export type UserActivityChangeEventsRow =
  | UserActivityChangeEventsRow_Employee
  | UserActivityChangeEventsRow_Entity
  | UserActivityChangeEventsRow_Other;

export const getIgnoreDataProperties = (
  row: UserActivityChangeEventsRow,
): string[] => {
  const { targetType, auditType } = row;

  switch (targetType) {
    case AuditTargetType.Employee: {
      switch (auditType) {
        case AuditType.AbsenceLong:
        case AuditType.AbsenceShort:
          return [
            'id',
            'uid',
            'decidedDate',
            'deciderEmpFK',
            'enteredTime',
            'enteredUserFK',
            'supersededTime',
            'supersededReason',
            'supersededUserFK',
          ];
        case AuditType.Employment:
          return [
            'id',
            'uid',
            'uuid',
            'updatedTime',
            'enteredTime',
            'enteredUserFK',
            'supersededTime',
            'supersededReason',
            'supersededUserFK',
          ];
        case AuditType.EmploymentPosition:
          return [
            'enteredTime',
            'enteredUserFK',
            'supersededTime',
            'supersededReason',
            'supersededUserFK',
          ];
        case AuditType.PersonProfile:
          return ['id', 'created_time', 'updated_time'];
        case AuditType.AuthUserRole:
          return ['ips', 'user_fk'];
        default:
          return [];
      }
    }
    default:
      return [];
  }
};

export const getCensorDataProperties = (
  row: UserActivityChangeEventsRow,
): string[] => {
  switch (row.targetType) {
    case AuditTargetType.Employee: {
      switch (row.auditType) {
        case AuditType.PersonProfile:
          return ['ssn'];
        default:
          return [];
      }
    }
    default:
      return [];
  }
};

export const censorDataPoints = (
  dps: UserActivityLogEventDataPoint[],
  censorKeys: string[],
): UserActivityLogEventDataPoint[] => {
  if (censorKeys.length > 0) {
    const censorString = '********';
    const censorValue = <TValue>(value: TValue): TValue | string =>
      !!value ? censorString : value;

    return dps.map((dp) => {
      if (censorKeys.includes(dp.key)) {
        switch (dp.differenceType) {
          case 'CREATE': {
            return {
              ...dp,
              value: `=> ${censorString}`,
            };
          }
          case 'CHANGE': {
            return {
              ...dp,
              value: `${censorString} => ${censorString}`,
            };
          }
          case 'REMOVE': {
            return {
              ...dp,
              value: `${censorString} =>`,
            };
          }
          default: {
            return {
              ...dp,
              value: censorValue(dp.value),
            };
          }
        }
      }

      return dp;
    });
  }

  return dps;
};

export const censorUserActivityChangeEventRowDataPoints = (
  row: UserActivityChangeEventsRow,
): UserActivityChangeEventsRow => {
  const censorProps = getCensorDataProperties(row);

  if (censorProps.length > 0) {
    return {
      ...row,
      dataPoints: censorDataPoints(row.dataPoints, censorProps),
      dataPointChanges: censorDataPoints(row.dataPointChanges, censorProps),
    };
  }

  return row;
};

const getKeyStringFromValues = (keyVals: (string | undefined)[]) => {
  return keyVals.filter((v) => !!v).join('-');
};

const resolveChangeTrackingKey = (
  event: Omit<UserActivityChangeEventsRow, 'changeTrackingKey'>,
) => {
  const { targetType, targetSubType, auditType, data } = event;

  const resolveTrackingAuditType = () => {
    switch (targetType) {
      case AuditTargetType.Employee: {
        switch (auditType) {
          case AuditType.EmployeeWorkContact:
          case AuditType.EmployeeWorkEmail:
          case AuditType.EmployeeWorkPhone:
          case AuditType.EmployeeWorkMobile: {
            return AuditType.EmployeeContactWork;
          }
          case AuditType.EmployeeHomeContact:
          case AuditType.EmployeeHomeAddress:
          case AuditType.EmployeeHomeEmail:
          case AuditType.EmployeeHomePhone: {
            return AuditType.EmployeeContactHome;
          }
          case AuditType.EmployeeOtherContact:
          case AuditType.EmployeeOtherAddress:
          case AuditType.EmployeeOtherEmail:
          case AuditType.EmployeeOtherPhone: {
            return AuditType.EmployeeContactOther;
          }
          default:
            return auditType;
        }
      }
      default:
        return auditType;
    }
  };

  const resolveAdditionalDataMatchIdentifiers = () => {
    switch (targetType) {
      case AuditTargetType.Entity: {
        const { entity_fk, entity_id } = data as AuditSummaryData_EntityData;

        return [entity_fk, entity_id].join('-');
      }
      case AuditTargetType.Employee: {
        switch (auditType) {
          case AuditType.AbsenceShort:
          case AuditType.AbsenceLong: {
            return (data as AuditSummaryData_Employee_Absences_Data).uid;
          }
          default:
            return undefined;
        }
      }
      default:
        return undefined;
    }
  };

  return getKeyStringFromValues([
    targetType,
    targetSubType,
    resolveTrackingAuditType(),
    resolveAdditionalDataMatchIdentifiers(),
  ]);
};

export const convertAuditSummaryDataToUserActivityChangeEventsRows = (
  data: AuditSummaryData[],
  getEmployeeById: (employeeId?: number) => Employees | undefined,
  getUnitByIdIncludeDeleted: (unitId?: number) => OrganizationUnit | undefined,
  getTranslatedEventData: (
    row: UserActivityChangeEventsRow,
  ) => UserActivityLogEventDataPoint[],
): UserActivityChangeEventsRow[] => {
  const getEmployeeName = (employee: Employees | undefined) =>
    !!employee ? `${employee.fEmpFirstName} ${employee.fEmpLastName}` : '';

  const getEmployeeDetails = (empId?: string | number) => {
    const employeeId = getNumberOrUndefined(empId);
    const employee = getEmployeeById(employeeId);
    const unit = getUnitByIdIncludeDeleted(employee?.fEmpUnit);

    return {
      employeeId,
      employee,
      unit,
    };
  };

  const removeIgnoredDataPropertiesFromRow = (
    row: UserActivityChangeEventsRow,
  ): UserActivityChangeEventsRow => {
    // Remove ignored data props
    const ignoreProps = getIgnoreDataProperties(row);

    const detailsData = Object.fromEntries(
      Object.entries(row.data).filter(([key]) => !ignoreProps.includes(key)),
    );

    return {
      ...row,
      data: detailsData,
    };
  };

  return processEventGroups(
    data.map(
      (summary: AuditSummaryData, index): UserActivityChangeEventsRow => {
        const {
          auditType,
          operation,
          timestamp,
          subjectEmployeeId,
          targetId,
          targetType,
          data: summaryData,
          transactionId,
        } = summary;

        const user = getEmployeeDetails(subjectEmployeeId);

        const targetEmployee = ['employee', 'entity'].includes(targetType)
          ? getEmployeeDetails(targetId)
          : undefined;

        const resolveTargetSubType = () => {
          if (targetType === AuditTargetType.Entity) {
            // Get Custom Data type
            return (summaryData as AuditSummaryData_EntityData).entity_fk;
          }
          return undefined;
        };

        const event: Omit<UserActivityChangeEventsRow, 'changeTrackingKey'> = {
          id: `row-id-${index}`,
          userId: user.employeeId,
          userName:
            user?.employeeId && user.employeeId > 0
              ? getEmployeeName(user.employee)
              : 'SYSTEM',
          userUnit: user.unit?.fOrgUnitName ?? '',
          auditType,
          operation,
          timestamp: convertToDateOrUndefined(timestamp),
          targetId: getNumberOrUndefined(targetId),
          targetType,
          targetSubType: resolveTargetSubType(),
          targetName: getEmployeeName(targetEmployee?.employee),
          targetUnit: targetEmployee?.unit?.fOrgUnitName ?? '',
          data: summaryData,
          dataPoints: [],
          dataPointChanges: [],
          transactionId,
        };

        const results: UserActivityChangeEventsRow =
          removeIgnoredDataPropertiesFromRow({
            ...event,
            changeTrackingKey: resolveChangeTrackingKey(event),
          });

        return {
          ...results,
          dataPoints: getTranslatedEventData(results),
        };
      },
    ),
  );
};

const processEventGroups = (eventRows: UserActivityChangeEventsRow[]) => {
  const { eventGroups, singleEvents } = eventRows.reduce(
    (acc, row) => {
      const { targetType, auditType, changeTrackingKey, timestamp } = row;

      const eventGroupKey = getKeyStringFromValues([
        changeTrackingKey,
        timestamp?.toISOString(),
      ]);

      switch (targetType) {
        case AuditTargetType.Entity: {
          if (acc.eventGroups.hasOwnProperty(eventGroupKey)) {
            acc.eventGroups[eventGroupKey].push(row);
          } else {
            acc.eventGroups = {
              ...acc.eventGroups,
              [eventGroupKey]: [row],
            };
          }

          break;
        }
        case AuditTargetType.Employee: {
          switch (auditType) {
            case AuditType.AuthUserRole:
            case AuditType.EmployeeWorkContact:
            case AuditType.EmployeeWorkEmail:
            case AuditType.EmployeeWorkPhone:
            case AuditType.EmployeeWorkMobile:
            case AuditType.EmployeeHomeContact:
            case AuditType.EmployeeHomeAddress:
            case AuditType.EmployeeHomeEmail:
            case AuditType.EmployeeHomePhone:
            case AuditType.EmployeeOtherContact:
            case AuditType.EmployeeOtherAddress:
            case AuditType.EmployeeOtherEmail:
            case AuditType.EmployeeOtherPhone: {
              if (acc.eventGroups.hasOwnProperty(eventGroupKey)) {
                acc.eventGroups[eventGroupKey].push(row);
              } else {
                acc.eventGroups = {
                  ...acc.eventGroups,
                  [eventGroupKey]: [row],
                };
              }

              break;
            }
            default: {
              acc.singleEvents.push(row);
              break;
            }
          }
          break;
        }
        default: {
          acc.singleEvents.push(row);
          break;
        }
      }

      return acc;
    },
    {
      eventGroups: {},
      singleEvents: [],
    } as {
      eventGroups: { [p: string]: UserActivityChangeEventsRow[] };
      singleEvents: UserActivityChangeEventsRow[];
    },
  );

  const resolveGroupRowsOperation = (
    groupRows: UserActivityChangeEventsRow[],
  ) => {
    const operations = groupRows.map(({ operation }) => operation);
    const isOperation = (operation: AuditOperation) =>
      operations.some((o) => o === operation);

    const hasInsert = isOperation(AuditOperation.Insert);
    const hasUpdate = isOperation(AuditOperation.Update);
    const hasDelete = isOperation(AuditOperation.Delete);

    if (hasInsert && !hasDelete && !hasUpdate) {
      return AuditOperation.Insert;
    } else if (!hasInsert && hasDelete && !hasUpdate) {
      return AuditOperation.Delete;
    } else {
      return AuditOperation.Update;
    }
  };

  const getGroupDataPointsByOperation =
    (groupRows: UserActivityChangeEventsRow[]) =>
    (auditOperation: AuditOperation): UserActivityLogEventDataPoint[] =>
      groupRows
        .filter(({ operation }) => operation === auditOperation)
        .map(({ dataPoints }): UserActivityLogEventDataPoint[] => dataPoints)
        .flat();

  const resolveGroupRowsDataPointsCustomData = (
    groupRows: UserActivityChangeEventsRow[],
  ) => {
    const operation = resolveGroupRowsOperation(groupRows);

    const getDataPointsByOperation = getGroupDataPointsByOperation(groupRows);

    const groupDataPoints = {
      deleted: getDataPointsByOperation(AuditOperation.Delete),
      inserted: getDataPointsByOperation(AuditOperation.Insert),
      all: uniqBy(
        groupRows.map(({ dataPoints }) => dataPoints).flat(),
        (dp) => dp.key,
      ),
    };

    return {
      operation,
      dataPoints: groupDataPoints.inserted,
      dataPointChanges: resolveDataPointChanges(
        groupDataPoints.deleted,
        groupDataPoints.inserted,
      ),
    };
  };

  const resolveGroupRowsDataPoints_Contacts = (
    groupRows: UserActivityChangeEventsRow[],
  ): Pick<
    UserActivityChangeEventsRow,
    'operation' | 'dataPoints' | 'dataPointChanges'
  > => {
    const operation = resolveGroupRowsOperation(groupRows);

    const getDataPointsByOperation = getGroupDataPointsByOperation(groupRows);

    const groupDataPoints = {
      deleted: getDataPointsByOperation(AuditOperation.Delete),
      inserted: getDataPointsByOperation(AuditOperation.Insert),
      updated: getDataPointsByOperation(AuditOperation.Update),
      all: uniqBy(
        groupRows.map(({ dataPoints }) => dataPoints).flat(),
        (dp) => dp.key,
      ),
    };

    const operationDataPoints = (): Pick<
      UserActivityChangeEventsRow,
      'dataPoints' | 'dataPointChanges'
    > => {
      const noDataPoints = {
        dataPoints: [],
        dataPointChanges: [],
      };

      switch (operation) {
        case AuditOperation.Insert: {
          return {
            dataPoints: groupDataPoints.inserted,
            dataPointChanges: resolveDataPointChanges(
              groupDataPoints.deleted,
              groupDataPoints.inserted,
            ),
          };
        }
        case AuditOperation.Delete: {
          return {
            ...noDataPoints,
            dataPointChanges: resolveDataPointChanges(
              groupDataPoints.deleted,
              groupDataPoints.inserted,
            ),
          };
        }
        case AuditOperation.Update: {
          return {
            ...noDataPoints,
            dataPoints: groupDataPoints.updated,
          };
        }
        default:
          return noDataPoints;
      }
    };

    return {
      operation,
      ...operationDataPoints(),
    };
  };

  const mergeDataPointsKeyMatchValues = (
    dps: UserActivityLogEventDataPoint[],
  ) => {
    const valueMapping = dps.reduce((acc, dp) => {
      const { key, value } = dp;

      return {
        ...acc,
        [key]: acc.hasOwnProperty(key) ? acc[key].concat(value) : [value],
      };
    }, {} as { [p: string]: UserActivityLogEventDataPoint['value'][] });

    return uniqBy(dps, (dp) => dp.key).map(
      (dp): UserActivityLogEventDataPoint => {
        return {
          ...dp,
          value: valueMapping[dp.key].join(', '),
        };
      },
    );
  };

  const resolveCompactedGroupRows_Contacts = (
    groupRows: UserActivityChangeEventsRow[],
  ) => {
    const getDataPointsByOperation = getGroupDataPointsByOperation(groupRows);

    return uniqBy(groupRows, 'operation').map((row) => {
      return {
        ...row,
        dataPoints: mergeDataPointsKeyMatchValues(
          getDataPointsByOperation(row.operation),
        ),
      };
    });
  };

  const compactedRows = Object.values(eventGroups).map(
    (groupRows): UserActivityChangeEventsRow => {
      const firstRow = groupRows[0];
      const { targetType, auditType } = firstRow;

      switch (targetType) {
        case AuditTargetType.Entity: {
          return {
            ...firstRow,
            ...resolveGroupRowsDataPointsCustomData(groupRows),
          };
        }
        case AuditTargetType.Employee: {
          switch (auditType) {
            case AuditType.AuthUserRole: {
              const getDataPointsByOperation =
                getGroupDataPointsByOperation(groupRows);

              const newGroupRows = uniqBy(groupRows, 'operation').map((row) => {
                return {
                  ...row,
                  dataPoints: mergeDataPointsKeyMatchValues(
                    getDataPointsByOperation(row.operation),
                  ),
                };
              });

              return {
                ...firstRow,
                ...resolveGroupRowsDataPointsCustomData(newGroupRows),
              };
            }
            case AuditType.EmployeeWorkContact:
            case AuditType.EmployeeWorkEmail:
            case AuditType.EmployeeWorkPhone:
            case AuditType.EmployeeWorkMobile:
            case AuditType.EmployeeHomeContact:
            case AuditType.EmployeeHomeAddress:
            case AuditType.EmployeeHomeEmail:
            case AuditType.EmployeeHomePhone:
            case AuditType.EmployeeOtherContact:
            case AuditType.EmployeeOtherAddress:
            case AuditType.EmployeeOtherEmail:
            case AuditType.EmployeeOtherPhone: {
              const contactAuditType = () => {
                switch (auditType) {
                  case AuditType.EmployeeWorkContact:
                  case AuditType.EmployeeWorkEmail:
                  case AuditType.EmployeeWorkPhone:
                  case AuditType.EmployeeWorkMobile: {
                    return AuditType.EmployeeContactWork;
                  }
                  case AuditType.EmployeeHomeContact:
                  case AuditType.EmployeeHomeAddress:
                  case AuditType.EmployeeHomeEmail:
                  case AuditType.EmployeeHomePhone: {
                    return AuditType.EmployeeContactHome;
                  }
                  case AuditType.EmployeeOtherContact:
                  case AuditType.EmployeeOtherAddress:
                  case AuditType.EmployeeOtherEmail:
                  case AuditType.EmployeeOtherPhone: {
                    return AuditType.EmployeeContactOther;
                  }
                  default:
                    return auditType;
                }
              };

              return {
                ...firstRow,
                auditType: contactAuditType(),
                ...resolveGroupRowsDataPoints_Contacts(
                  resolveCompactedGroupRows_Contacts(groupRows),
                ),
              };
            }
            default: {
              return firstRow;
            }
          }
        }
        default: {
          return firstRow;
        }
      }
    },
  );

  return singleEvents.concat(compactedRows);
};
